import { useEffect, useRef, useState } from 'preact/hooks'
import Icon from 'snippets/icon/icon'
import LoaderSpin from 'snippets/loader-spin/loader-spin'

function QuantityInput({ min = 0, max = 9999, quantity, setQuantity, discountAllocations, disabled = false, classWrap = '', classButton = 'w-3.25 h-7 p-0', isLoading = false, showTrashAtOne = false, onRemove }) {
  const [value, setValue] = useState(quantity)
  const [message, setMessage] = useState(null)
  const inputRef = useRef(null)

  const onChange = (count) => {
    const current = value + count

    if (discountAllocations) {
      setQuantity(current)

      return
    }

    if (current >= min) {
      setValue(current)
      setQuantity(current)
    }
  }

  const handleMinusClick = () => {
    if (showTrashAtOne && value === 1 && onRemove) {
      // When showing trash icon and quantity is 1, call remove function
      onRemove()
    } else {
      // Otherwise, decrease quantity
      onChange(-1)
    }
  }

  const onValidate = (value) => {
    return /^\d+$/.test(value)
  }

  const onChangeInput = (e) => {
    e.preventDefault()

    const currentQuantity = parseInt(e.target.value)
    const isValidated = onValidate(currentQuantity)

    if (isNaN(currentQuantity)) {
      inputRef.current.value = value

      return
    }

    if (!discountAllocations) {
      setValue(currentQuantity)
    }

    if (inputRef && inputRef.current && !discountAllocations) {
      inputRef.current.value = currentQuantity
    }

    if (!isValidated) {
      if (currentQuantity !== 0 && currentQuantity < min) {
        setMessage('Enter a positive numbers')
      } else {
        setMessage('Enter only number')
      }

      return
    }

    setQuantity(currentQuantity)

    if (currentQuantity <= max && currentQuantity >= 0) {
      setMessage(null)
    } else {
      changeMessage()
    }
  }

  const changeMessage = () => {
    if (max > 1) {
      setMessage(`We have ${max} items`)
    } else {
      setMessage('We have only 1 item')
    }
  }

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      onChangeInput(event)
    }
  }

  useEffect(() => {
    if (quantity >= min) {
      setValue(quantity)
    }
  }, [quantity])

  return (
    <>
      <div className={`flex h-9 items-center justify-between overflow-hidden ${classWrap}`}>
        <button
          type="button"
          name="minus"
          className={`flex items-center justify-center ${classButton} ${
            // Show as enabled when trash icon is shown at quantity 1
            showTrashAtOne && value === 1 && onRemove
              ? ''
              : (value <= min || disabled || isLoading)
                ? 'pointer-events-none cursor-not-allowed'
                : ''
          }`}
          disabled={
            // Enable button when trash icon is shown at quantity 1
            showTrashAtOne && value === 1 && onRemove
              ? false
              : (value <= min || disabled || isLoading)
          }
          onClick={handleMinusClick}
          aria-label={showTrashAtOne && value === 1 ? 'remove-item' : 'previous-page'}
        >
          {showTrashAtOne && value === 1 ? (
            <Icon
              viewBox="0 0 14 14"
              name="icon-trash"
              className="w-[14px] h-[14px] text-grey-600"
            />
          ) : (
            <Icon
              viewBox="0 0 16 16"
              name="icon-minus"
              className="w-3.25 h-3.25 text-grey-600"
            />
          )}
        </button>
        <div className="relative w-6.5 h-7">
          {!isLoading ? (
            <input
              type="number"
              ref={inputRef}
              value={value}
              className="w-6.5 h-7 border-none p-0 text-center text-xxxs text-default tracking-base !shadow-none !ring-0"
              min={min}
              max={max}
              onChange={onChangeInput}
              onBlur={onChangeInput}
              readonly={disabled}
              aria-label="page-number"
              onKeyDown={handleKeyDown}
            />
          ) : (
            <LoaderSpin width="4" height="4" />
          )}
        </div>
        <button
          type="button"
          name="plus"
          className={`flex items-center justify-center ${classButton} ${value < min || value >= max || disabled || isLoading ? 'pointer-events-none cursor-not-allowed opacity-50' : ''
            }`}
          disabled={value < min || value === max || disabled || isLoading}
          onClick={() => onChange(1)}
          aria-label="next-page"
        >
          <Icon viewBox="0 0 16 16" name="icon-plus" className="w-3.25 h-3.25 text-grey-600" />
        </button>
      </div>
      {message && <span className="self-center text-xs text-error">{message}</span>}
    </>
  )
}

export default QuantityInput
